import { z } from 'zod';

import { type BriefContent, type PublishedBrief, briefContentSchema } from './brief.ts';
import { type FollowUpRecord, followUpTextSchema } from './brief-follow-ups.ts';
import {
  type BriefChangeNotes,
  type BriefFramingMarkers,
  briefChangeNotesSchema,
  briefFramingMarkers,
} from './brief-framing.ts';

/**
 * One published Brief version as a reader outside Sigora is shown it — the PDF export, and the
 * stakeholder view that reuses it: the study's title and its owner's name, the version and when it
 * was written, when the snapshot was taken, whether the participants were simulated, the content as
 * the owner framed it, the framing markers, the revision the version's Updated tags come from, and
 * the follow-ups' record — all read in one snapshot, so nothing in it is of another committed state.
 *
 * It holds no history, no chat, no synthesis request, no author, no email and none of the owner's
 * edit state: no bullet they hid, none of what Sigora wrote under their framing (the content's
 * `verdict.sigora_headline` and `recommendation.sigora_order` are `null`), and no account of where a
 * revision could not keep their framing (`lastRevision.notes.framing` is empty). That the owner
 * framed a block is said only by `markers`. The content's ids and the record's follow-up ids stay:
 * they are the keys the Brief's components join on, opaque and never rendered as text.
 */
export interface BriefSnapshot {
  studyTitle: string;
  /** The display name of the study's owner, read in the snapshot. */
  ownerName: string;
  version: number;
  /** When this version was written (ISO). */
  versionCreatedAt: string;
  /** When the snapshot was taken: its transaction's `now()` (ISO). */
  capturedAt: string;
  /** Whether the participants were generated rather than recruited (`content.method.simulated`). */
  simulated: boolean;
  /**
   * The version's content as the owner framed it, with the bullets they hid taken out and nothing
   * of what Sigora wrote under their framing: every bullet shows, and `sigora_headline` and
   * `sigora_order` are `null`.
   */
  content: BriefContent;
  /**
   * Which blocks show the owner's framing, worked out from the content as stored, before anything
   * was taken out of it — the content no longer says.
   */
  markers: BriefFramingMarkers;
  /**
   * Sigora's last revision of the Brief, which its Updated tags are drawn from — its note and the
   * blocks it changed, with `framing` always empty; `null` while there is none.
   */
  lastRevision: { version: number; createdAt: string; notes: BriefChangeNotes | null } | null;
  /** What the follow-ups added to the Brief as it stood in the snapshot; empty when there are none. */
  record: FollowUpRecord;
}

const followUpQuoteSchema = z.strictObject({
  quote_id: z.string().min(1),
  participant: z.string().min(1),
  text: z.string().min(1),
});

const followUpOutcomeSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('note'), rqId: z.string().min(1) }),
  z.strictObject({ kind: z.literal('log') }),
  z.strictObject({
    kind: z.literal('update_brief'),
    briefVersion: z.int().positive(),
    changed: z.boolean(),
  }),
]);

const followUpRecordSchema = z.strictObject({
  notes: z.array(
    z.strictObject({
      followUpId: z.string().min(1),
      rqId: z.string().min(1),
      finalizedAt: z.iso.datetime(),
      question: z.string(),
      text: followUpTextSchema,
      quotes: z.array(followUpQuoteSchema),
    }),
  ),
  log: z.array(
    z.strictObject({
      followUpId: z.string().min(1),
      question: z.string(),
      finalizedAt: z.iso.datetime(),
      outcome: followUpOutcomeSchema,
    }),
  ),
  followUpCount: z.int().nonnegative(),
});

/**
 * A `BriefSnapshot` as it crosses into a page that renders it. Strict throughout, so a key the
 * snapshot does not name — a history, an author — is refused rather than carried. It also holds the
 * guarantees a reader relies on: every bullet in the content shows, nothing Sigora wrote under the
 * owner's framing is there, a revision says nothing about the owner's framing, and `simulated` is
 * the Method's. The markers cannot be checked against the content, which no longer holds what they
 * were worked out from; `briefSnapshotOf` is what makes them right.
 */
export const briefSnapshotSchema: z.ZodType<BriefSnapshot> = z
  .strictObject({
    studyTitle: z.string(),
    ownerName: z.string(),
    version: z.int().positive(),
    versionCreatedAt: z.iso.datetime(),
    capturedAt: z.iso.datetime(),
    simulated: z.boolean(),
    content: briefContentSchema,
    markers: z.strictObject({
      verdict: z.boolean(),
      summary: z.boolean(),
      recommendation: z.boolean(),
    }),
    lastRevision: z
      .strictObject({
        version: z.int().positive(),
        createdAt: z.iso.datetime(),
        notes: briefChangeNotesSchema.nullable(),
      })
      .nullable(),
    record: followUpRecordSchema,
  })
  .superRefine((snapshot, context) => {
    if (snapshot.content.summary.some((item) => !item.visible)) {
      context.addIssue({
        code: 'custom',
        path: ['content', 'summary'],
        message: 'a snapshot holds only the bullets that show',
      });
    }
    if (snapshot.content.verdict.sigora_headline !== null) {
      context.addIssue({
        code: 'custom',
        path: ['content', 'verdict', 'sigora_headline'],
        message: "a snapshot holds the owner's headline and not Sigora's under it",
      });
    }
    if (snapshot.content.recommendation.sigora_order !== null) {
      context.addIssue({
        code: 'custom',
        path: ['content', 'recommendation', 'sigora_order'],
        message: "a snapshot holds the owner's order and not Sigora's under it",
      });
    }
    if ((snapshot.lastRevision?.notes?.framing.length ?? 0) > 0) {
      context.addIssue({
        code: 'custom',
        path: ['lastRevision', 'notes', 'framing'],
        message: "a snapshot's revision says nothing about the owner's framing",
      });
    }
    if (snapshot.simulated !== snapshot.content.method.simulated) {
      context.addIssue({
        code: 'custom',
        path: ['simulated'],
        message: 'a snapshot is simulated exactly when its Method says so',
      });
    }
  });

/**
 * The one way a published Brief becomes a `BriefSnapshot`: the version and its content as the owner
 * framed it — the bullets they hid taken out, and Sigora's headline and order under their framing
 * dropped — the markers worked out from the content as stored, so "Edited by you" still marks a
 * block whose edit the content no longer shows, its last revision with its framing changes dropped,
 * and its record, with the facts the caller read in the same snapshot. Parsed on the way out, so a
 * snapshot this code shaped wrongly fails here rather than in the page that renders it.
 */
export const briefSnapshotOf = (
  published: PublishedBrief,
  {
    studyTitle,
    ownerName,
    capturedAt,
  }: { studyTitle: string; ownerName: string; capturedAt: string },
): BriefSnapshot => {
  const { content, lastRevision } = published;

  return briefSnapshotSchema.parse({
    studyTitle,
    ownerName,
    version: published.version,
    versionCreatedAt: published.createdAt,
    capturedAt,
    simulated: content.method.simulated,
    content: {
      ...content,
      verdict: { ...content.verdict, sigora_headline: null },
      summary: content.summary.filter((item) => item.visible),
      recommendation: { ...content.recommendation, sigora_order: null },
    },
    markers: briefFramingMarkers(content),
    lastRevision:
      lastRevision === null
        ? null
        : {
            version: lastRevision.version,
            createdAt: lastRevision.createdAt,
            notes: lastRevision.notes === null ? null : { ...lastRevision.notes, framing: [] },
          },
    record: published.record,
  });
};

/** How much of a study's title an export's file name keeps, in characters. */
export const BRIEF_EXPORT_TITLE_CHARS = 80;

/** The file name an export's title is called when nothing of the study's title is left. Placeholder copy. */
const UNTITLED_EXPORT = 'Study';

/**
 * The file name a Brief's PDF is saved under: `<title> - Brief v<n> - <YYYY-MM-DD>.pdf`, dated on the
 * day it was captured in UTC. The title is cut to `BRIEF_EXPORT_TITLE_CHARS` characters — whole
 * characters, never half of one — and each character a file system refuses in a name (`/\:*?"<>|`)
 * and each control character is replaced by `-`, so the name saves as it reads on any system. A
 * title with nothing left is `Study`.
 */
export const briefExportFilename = (title: string, version: number, capturedAt: string): string => {
  const safe = title.trim().replace(/[/\\:*?"<>|\p{Cc}]/gu, '-');
  const cut = Array.from(safe).slice(0, BRIEF_EXPORT_TITLE_CHARS).join('').trim();
  const day = new Date(capturedAt).toISOString().slice(0, 10);

  return `${cut === '' ? UNTITLED_EXPORT : cut} - Brief v${String(version)} - ${day}.pdf`;
};
